/**
 * captureQueue — offline-safe email-capture tap queue.
 *
 * Background (user 2026-06-12): the store wifi at Five Below drops
 * constantly. When crew tap "+1" during a dead pocket, the tally request
 * fails silently and that capture is lost. This queue stores failed taps
 * to localStorage and silent-syncs them the moment the browser is back
 * online (or on a 30s tick).
 *
 * Design notes:
 *   - One queue per user — stored as `wow_capture_queue_v1`. We don't
 *     namespace by crew_id because each device only logs in as one
 *     person at a time, and we explicitly drop the queue on logout
 *     (App.js handleLogout already wipes localStorage).
 *   - Each entry is `{id, crew_id, date, action, queued_at}`.
 *   - We retry sequentially (one POST at a time) so the backend's
 *     internal milestone counters increment in real order. The user
 *     doesn't get retroactive confetti — it's a silent reconciliation.
 *   - Adds: action="add". Subs: action="subtract".
 */

const KEY = "wow_capture_queue_v1";

function _read() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function _write(arr) {
  try {
    localStorage.setItem(KEY, JSON.stringify(arr));
  } catch {
    // Quota or private mode — best-effort. We've at least kept the
    // optimistic UI count, and the next live tap will overwrite the
    // tally from the server anyway.
  }
  // Let any subscribed UI re-render.
  try {
    window.dispatchEvent(new CustomEvent("wow:capture-queue-change", { detail: { size: arr.length } }));
  } catch { /* no-op */ }
}

export function getQueue() {
  return _read();
}

export function getQueueSize() {
  return _read().length;
}

export function enqueueTap({ crew_id, date, action }) {
  const arr = _read();
  arr.push({
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    crew_id,
    date,
    action: action || "add",
    queued_at: new Date().toISOString(),
  });
  _write(arr);
}

export function clearQueue() {
  _write([]);
}

/**
 * Flush the queue by POSTing each entry sequentially to /tally.
 * `postFn(entry)` is a caller-supplied async fn that performs the
 * actual axios call — this keeps the helper unaware of the API base URL
 * (which lives in process.env on the caller's side).
 *
 * Returns the count of entries successfully flushed.
 * Any entry that 4xx's (e.g. 423 store-closed) is DROPPED so we don't
 * loop forever; transient 5xx / network errors are LEFT in queue.
 */
export async function flushQueue(postFn) {
  if (typeof navigator !== "undefined" && navigator.onLine === false) return 0;
  let queue = _read();
  if (queue.length === 0) return 0;

  let flushed = 0;
  // Walk a copy; mutate the persistent queue as we succeed/drop so a
  // mid-flush crash doesn't re-fire everything.
  const remaining = [...queue];
  while (remaining.length) {
    const entry = remaining[0];
    try {
      await postFn(entry);
      flushed += 1;
      remaining.shift();
      _write(remaining); // persist progress between calls
    } catch (e) {
      const status = e?.response?.status;
      if (status && status >= 400 && status < 500) {
        // The server permanently rejected this — drop it to prevent an
        // infinite loop. (Most common: 423 store-closed for taps that
        // were queued before close-of-day rolled over.)
        remaining.shift();
        _write(remaining);
        continue;
      }
      // Network or 5xx — leave the rest in queue and stop trying.
      break;
    }
  }
  return flushed;
}
